import { redactShopifyInstallTelemetry } from "@/lib/shopifyInstallTelemetry";
export type InstallInput = {
  ticket: string;
  accountId: string;
  bootstrapReady: boolean;
  workspaceIds: string[];
  selectedWorkspaceId: string;
  currentWorkspaceId: string;
  syncedWorkspaceId: string;
};
export type TrustedInstall = { shop_domain: string; oauth_app_id: "BACKUP" | "PRIMARY" };
export type InstallState =
  | {
      kind: "checking" | "missing" | "invalid" | "validation-error" | "success";
    }
  | { kind: "workspace-required" | "workspace-unavailable"; shop: string }
  | { kind: "switching"; shop: string }
  | { kind: "ready" | "connecting" | "retry"; shop: string }
  | { kind: "restricted"; shop: string; restriction?: InstallRestriction };
export type InstallRestriction = {
  message?: string | null;
  eligibilityMessage?: string | null;
  ctaAction?: string | null;
  upgradeRequired?: boolean;
  restrictionState?: string;
};
export type InstallDependencies = {
  resolve: (ticket: string) => Promise<TrustedInstall>;
  connect: (
    shop: string,
    ticket: string,
    workspaceId: string,
  ) => Promise<
    { success?: boolean; blockConnect?: boolean; alreadyConnected?: boolean; already_connected?: boolean } & InstallRestriction
  >;
  verifyReuse: (workspaceId: string, context: TrustedInstall) => Promise<boolean>;
  succeeded: (workspaceId: string) => void;
  changed: (state: InstallState) => void;
  isCurrent?: (input: InstallInput) => boolean;
};
function isRejectedInstallContext(error: unknown): boolean {
  const failure = error as {
    httpStatus?: number;
    response?: { status?: number; code?: string };
    code?: string;
    businessCode?: string;
  } | null;
  const status = failure?.httpStatus || failure?.response?.status;
  const code =
    failure?.businessCode || failure?.code || failure?.response?.code;
  return (
    [400, 401, 403, 404, 410].includes(status || 0) ||
    [
      "BAD_REQUEST",
      "UNAUTHORIZED",
      "FORBIDDEN",
      "400",
      "401",
      "403",
      "404",
      "410",
    ].includes(code || "")
  );
}

export class ShopifyInstallController {
  state: InstallState = { kind: "checking" };
  private input: InstallInput | null = null;
  private epoch = 0;
  private context: TrustedInstall | null = null;
  private key = "";
  constructor(private dependencies: InstallDependencies) {}
  private publish(state: InstallState): void {
    this.state = state;
    this.dependencies.changed(state);
  }
  private active(epoch: number, input: InstallInput): boolean {
    return (
      epoch === this.epoch &&
      (!this.dependencies.isCurrent || this.dependencies.isCurrent(input))
    );
  }
  update(input: InstallInput): void {
    const key = JSON.stringify(input);
    if (key === this.key) return;
    this.key = key;
    this.input = input;
    this.context = null;
    const epoch = ++this.epoch;
    if (!input.ticket) {
      this.publish({ kind: "missing" });
      return;
    }
    this.publish({ kind: "checking" });
    if (!input.bootstrapReady || !input.accountId) return;
    void this.dependencies
      .resolve(input.ticket)
      .then((context) => {
        if (!this.active(epoch, input)) return;
        if (!["BACKUP", "PRIMARY"].includes(context.oauth_app_id) || !context.shop_domain) {
          this.publish({ kind: "invalid" });
          return;
        }
        this.context = context;
        const shop = context.shop_domain;
        if (
          input.selectedWorkspaceId &&
          !input.workspaceIds.includes(input.selectedWorkspaceId)
        ) {
          this.publish({ kind: "workspace-unavailable", shop });
          return;
        }
        if (input.workspaceIds.length === 0) {
          this.publish({ kind: "workspace-required", shop });
          return;
        }
        const synchronized =
          input.workspaceIds.includes(input.selectedWorkspaceId) &&
          input.selectedWorkspaceId === input.currentWorkspaceId &&
          input.selectedWorkspaceId === input.syncedWorkspaceId;
        this.publish({ kind: synchronized ? "ready" : "switching", shop });
      })
      .catch((error) => {
        if (this.active(epoch, input))
          this.publish({
            kind: isRejectedInstallContext(error)
              ? "invalid"
              : "validation-error",
          });
      });
  }
  async connect(): Promise<void> {
    const input = this.input;
    const state = this.state;
    const epoch = this.epoch;
    if (
      !input ||
      !["ready", "retry", "restricted"].includes(state.kind) ||
      !("shop" in state) ||
      !this.active(epoch, input)
    )
      return;
    this.publish({ kind: "connecting", shop: state.shop });
    try {
      const result = await this.dependencies.connect(
        state.shop,
        input.ticket,
        input.selectedWorkspaceId,
      );
      if (!this.active(epoch, input)) return;
      if ((result.alreadyConnected === true || result.already_connected === true) && this.context) {
        const verified = await this.dependencies.verifyReuse(input.selectedWorkspaceId, this.context);
        if (!this.active(epoch, input)) return;
        if (!verified) {
          this.publish({kind: "retry", shop: state.shop});
          return;
        }
        this.publish({kind: "success"});
        this.dependencies.succeeded(input.selectedWorkspaceId);
        return;
      }
      if (result.blockConnect) {
        const safeMessage = (value?: string | null) =>
          typeof value === "string"
            ? redactShopifyInstallTelemetry(value)
                .split(input.ticket)
                .join("[REDACTED]")
                .slice(0, 500)
            : null;
        this.publish({
          kind: "restricted",
          shop: state.shop,
          restriction: {
            message: safeMessage(result.message || result.eligibilityMessage),
            ctaAction: result.ctaAction,
            upgradeRequired: result.upgradeRequired,
            restrictionState: result.restrictionState,
          },
        });
        return;
      }
      if (result.success !== true) {
        this.publish({ kind: "retry", shop: state.shop });
        return;
      }
      this.publish({ kind: "success" });
      this.dependencies.succeeded(input.selectedWorkspaceId);
    } catch {
      if (!this.active(epoch, input)) return;
      // A canceled popup is retryable, but a ticket/account that expired
      // while authorization was open needs a fresh launch from Shopify.
      try {
        await this.dependencies.resolve(input.ticket);
        if (this.active(epoch, input))
          this.publish({ kind: "retry", shop: state.shop });
      } catch (error) {
        if (this.active(epoch, input))
          this.publish({
            kind: isRejectedInstallContext(error)
              ? "invalid"
              : "validation-error",
          });
      }
    }
  }
  retryValidation(): void {
    if (this.state.kind !== "validation-error" || !this.input) return;
    this.key = "";
    this.update(this.input);
  }
  dispose(): void {
    ++this.epoch;
    this.key = "";
    this.input = null;
  }
}
